import { useCallback } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useMarkdownFolderResolverQuery,
	useMarkdownFolderResolverQuery$data,
	useMarkdownFolderResolverQuery$variables,
} from './__generated__/useMarkdownFolderResolverQuery.graphql.ts';
import { MarkdownImportError } from '../services/MarkdownImportService.ts';
import { createMarkdownFolder } from '../services/MarkdownFolderService.ts';

const MARKDOWN_FOLDER_SEARCH_QUERY = graphql`
	query useMarkdownFolderResolverQuery(
		$experience: String!
		$folderName: String!
		$filters: SearchFilterInput!
	) {
		search {
			search(experience: $experience, query: $folderName, first: 50, filters: $filters) {
				edges {
					node {
						title
						... on SearchConfluencePageBlogAttachment {
							confluenceEntity {
								... on ConfluenceFolder {
									folderId
									title
									allAncestors {
										__typename
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export type ResolveMarkdownFolderInput = {
	spaceId: string;
	spaceKey: string;
	folderName: string;
	signal: AbortSignal;
};

export type ResolveMarkdownFolder = (input: ResolveMarkdownFolderInput) => Promise<string>;

const searchMarkdownFolders = ({
	environment,
	variables,
	signal,
}: {
	environment: ReturnType<typeof useRelayEnvironment>;
	variables: useMarkdownFolderResolverQuery$variables;
	signal: AbortSignal;
}): Promise<useMarkdownFolderResolverQuery$data> => {
	return new Promise((resolve, reject) => {
		let subscription: { unsubscribe: () => void } | undefined;
		function cleanup() {
			signal.removeEventListener('abort', handleAbort);
		}
		function handleAbort() {
			subscription?.unsubscribe();
			cleanup();
			reject(new MarkdownImportError('aborted'));
		}

		signal.addEventListener('abort', handleAbort, { once: true });
		if (signal.aborted) {
			handleAbort();
			return;
		}

		subscription = fetchQuery<useMarkdownFolderResolverQuery>(
			environment,
			MARKDOWN_FOLDER_SEARCH_QUERY,
			variables,
			{ fetchPolicy: 'network-only' },
		).subscribe({
			next: (data) => {
				cleanup();
				resolve(data);
			},
			error: (error) => {
				cleanup();
				reject(error);
			},
		});
		if (signal.aborted) {
			subscription.unsubscribe();
		}
	});
};

const findExactRootFolderId = (
	data: useMarkdownFolderResolverQuery$data,
	folderName: string,
): string | undefined => {
	for (const edge of data.search?.search?.edges ?? []) {
		const folder = edge?.node?.confluenceEntity;
		if (folder?.title === folderName && folder.allAncestors?.length === 0) {
			return folder.folderId;
		}
	}

	return undefined;
};

export const useMarkdownFolderResolver = (): ResolveMarkdownFolder => {
	const { cloudId } = useSessionData();
	const environment = useRelayEnvironment();

	return useCallback(
		async ({ spaceId, spaceKey, folderName, signal }) => {
			if (signal.aborted) {
				throw new MarkdownImportError('aborted');
			}

			if (!expValEquals('twcg_2627_markdown_file_import_into_confluence', 'isEnabled', true)) {
				throw new MarkdownImportError('experiment_disabled');
			}

			const data = await searchMarkdownFolders({
				environment,
				signal,
				variables: {
					experience: 'confluence.searchInTree',
					folderName,
					filters: {
						entities: ['ati:cloud:confluence:folder'],
						locations: [`ari:cloud:confluence::site/${cloudId}`],
						confluenceFilters: {
							spacesFilter: [spaceKey],
							contentStatuses: ['CURRENT'],
							titleMatchOnly: true,
						},
					},
				},
			});
			if (signal.aborted) {
				throw new MarkdownImportError('aborted');
			}

			const existingFolderId = findExactRootFolderId(data, folderName);
			if (existingFolderId) {
				return existingFolderId;
			}

			return createMarkdownFolder({ spaceId, title: folderName, signal });
		},
		[cloudId, environment],
	);
};
